import { lazy, Suspense } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/use-auth';
import { useRealtimeNotifications } from '../hooks/use-notifications';
import { useRealtimeChanges } from '../hooks/use-realtime-changes';
import { useIsAdmin, useIsAssignedTasksOnly, usePermission } from '../hooks/use-permission';
import type { PermissionFlagValue } from '@task-tracker/shared';
import { getDefaultAuthenticatedPath } from '../lib/page-access';
import { useUiStore } from '../stores/ui.store';
import { cn } from '../lib/utils';

/** Lazy because App.tsx imports AuthGuard eagerly (it wraps every protected
 *  route), so a static import here pulls the sidebar and its Radix menu /
 *  dropdown / scroll-area dependencies into the entry chunk - onto /login,
 *  which never renders a sidebar. It suspends beside the content region rather
 *  than in front of it, so this costs an authenticated load nothing visible:
 *  the route chunk is still fetched in parallel, and RoutePrefetcher already
 *  warms these at idle. */
const AppSidebar = lazy(() => import('./layout/sidebar').then((m) => ({ default: m.AppSidebar })));

interface AuthGuardProps {
  children: React.ReactNode;
  fullWidth?: boolean;
  requiredPermission?: PermissionFlagValue;
  /** If true, roles with project_visibility='assigned_tasks' cannot access this
   * route and will be redirected to their allowed landing page. Use for
   * Dashboard / Reports / Team / Resources surfaces. */
  blockAssignedTasksOnly?: boolean;
}

/** Shown only while a route's JS chunk is in flight — a genuinely empty state,
 *  distinct from a data load. Deliberately quiet: chunks are prefetched at idle
 *  (see RoutePrefetcher), so in the common case this never paints at all. */
function RouteChunkFallback() {
  return (
    <div className="flex min-h-[60vh] items-center justify-center" aria-busy="true">
      <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
    </div>
  );
}

/** Width-matched stand-in for the sidebar chunk. Mirrors the real <aside>'s
 *  responsive visibility and collapse width so nothing reflows on arrival. */
function SidebarPlaceholder() {
  const desktopCollapsed = useUiStore((state) => state.desktopCollapsed);
  return (
    <aside
      aria-hidden="true"
      className={cn(
        'hidden md:flex flex-col glass',
        desktopCollapsed ? 'w-[var(--sidebar-width-collapsed)]' : 'w-[var(--sidebar-width)]',
      )}
    />
  );
}

/**
 * Wraps protected routes. Redirects to /login if not authenticated.
 * Preserves the intended destination so we can redirect back after login.
 */
export function AuthGuard({
  children,
  fullWidth = false,
  requiredPermission,
  blockAssignedTasksOnly = false,
}: AuthGuardProps) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  const isAdmin = useIsAdmin();
  const assignedTasksOnly = useIsAssignedTasksOnly();
  const canViewDashboard = usePermission('view_dashboard');
  const hasRequiredPermission = usePermission(requiredPermission ?? 'view_dashboard');
  useRealtimeNotifications();
  useRealtimeChanges();

  const fallbackPath = getDefaultAuthenticatedPath({
    isAdmin,
    assignedTasksOnly,
    canViewDashboard,
    canViewTeam: false,
    canViewReports: false,
  });

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (blockAssignedTasksOnly && assignedTasksOnly) {
    return <Navigate to={fallbackPath} replace />;
  }

  if (requiredPermission && !hasRequiredPermission) {
    return <Navigate to={fallbackPath} replace />;
  }

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      {/* Its own boundary, not the content one below: without this the lazy
          sidebar would suspend up to the router-level Suspense in App.tsx and
          blank the whole page. The fallback mirrors the real <aside>'s width so
          the content region does not shift when the chunk lands. */}
      <Suspense fallback={<SidebarPlaceholder />}>
        <AppSidebar />
      </Suspense>
      <main className="flex-1 overflow-y-auto pt-0 md:pt-0">
        {/* Mobile: add top padding for fixed header */}
        <div className="pt-14 md:pt-0">
          {/* Suspends here rather than at the router so the sidebar stays
              painted while a lazy route chunk loads. Only the content region
              shows the fallback, which keeps navigation from flashing a
              full-screen spinner. */}
          <Suspense fallback={<RouteChunkFallback />}>
            {fullWidth ? children : <div className="mx-auto max-w-7xl p-4 md:p-6">{children}</div>}
          </Suspense>
        </div>
      </main>
    </div>
  );
}
